import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import { StaffLogin } from '../components/staff/StaffLogin';
import { ApplicationCard } from '../components/staff/ApplicationCard';
import { ResetLimitButton } from '../components/staff/ResetLimitButton';
import { SlidingFeeCard } from '../components/staff/SlidingFeeCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

export default function StaffApplications() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [data, setData] = useState(null);
  const [sfdp, setSfdp] = useState(null);

  const loadSfdp = useCallback(() => {
    axios.get(`${API}/sliding-fee/applications`, opts).then((res) => setSfdp(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    axios.get(`${API}/preceptorship`, opts).then((res) => setData(res.data))
      .catch(() => setAuthState(false));
  }, []);

  useEffect(() => {
    if (authState) { load(); loadSfdp(); return; }
    axios.get(`${API}/auth/me`, opts)
      .then((res) => { setUser(res.data); setAuthState(true); })
      .catch(() => setAuthState(false));
  }, [authState, load, loadSfdp]);

  const changeStatus = async (id, status, sendEmail) => {
    try {
      const res = await axios.patch(`${API}/preceptorship/${id}/status`, { status, send_email: sendEmail }, opts);
      if (sendEmail && !res.data.email_sent) {
        toast.error(`Marked ${status}, but the email to the student could not be sent.`);
      } else {
        toast.success(sendEmail ? `Marked ${status} and emailed the student` : `Application marked ${status}`);
      }
      load();
    } catch { toast.error('Could not update status'); }
  };

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/preceptorship/${id}/notes`, { notes }, opts);
      toast.success('Notes saved');
      load();
    } catch { toast.error('Could not save notes'); }
  };

  const changeSfdpStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/sliding-fee/applications/${id}/status`, { status }, opts);
      toast.success(`Sliding fee application marked ${status}`);
      loadSfdp();
    } catch { toast.error('Could not update status'); }
  };

  const resetLimit = async () => {
    try {
      await axios.post(`${API}/preceptorship/reset-count`, {}, opts);
      toast.success('Application limit reset. The form is open again.');
      load();
    } catch { toast.error('Could not reset the limit'); }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, opts).catch(() => {});
    setUser(null); setData(null); setAuthState(false);
  };

  if (authState === null) return <div className="min-h-screen pt-20" />;
  if (authState === false) return <StaffLogin />;

  const full = data && data.count >= data.cap;
  return (
    <div className="min-h-screen pt-28 pb-20 px-4 bg-gray-50" data-testid="staff-applications-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Staff Applications</h1>
            <p className="text-gray-600 mt-1" data-testid="staff-user-email">Signed in as {user?.email}</p>
          </div>
          <button onClick={logout} data-testid="staff-logout-btn"
            className="inline-flex items-center gap-2 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-white transition-colors">
            <LogOut size={18} /> Sign Out
          </button>
        </div>

        <Tabs defaultValue="preceptorship">
          <TabsList className="mb-6">
            <TabsTrigger value="preceptorship" data-testid="staff-tab-preceptorship">NP Preceptorships</TabsTrigger>
            <TabsTrigger value="sliding-fee" data-testid="staff-tab-sliding-fee">Sliding Fee</TabsTrigger>
          </TabsList>

          <TabsContent value="preceptorship">
            {data && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-8 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Applications counted toward this term's limit</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="staff-cap-count">{data.count} / {data.cap}</p>
                  <p className={`text-sm font-semibold mt-1 ${full ? 'text-amber-700' : 'text-green-700'}`} data-testid="staff-form-status">
                    {full ? 'Form is closed (limit reached)' : 'Form is open on the website'}
                  </p>
                </div>
                <ResetLimitButton onConfirm={resetLimit} />
              </div>
            )}
            <div className="space-y-4" data-testid="staff-application-list">
              {data && data.applications.length === 0 && <p className="text-gray-600">No applications yet.</p>}
              {data?.applications.map((app) => (
                <ApplicationCard key={app.id} app={app} onStatusChange={changeStatus} onSaveNotes={saveNotes} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="sliding-fee">
            <div className="space-y-4" data-testid="staff-sfdp-list">
              {sfdp && sfdp.length === 0 && <p className="text-gray-600">No sliding fee applications yet.</p>}
              {sfdp?.map((app) => (
                <SlidingFeeCard key={app.confirmationId} app={app} onStatusChange={changeSfdpStatus} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
